import { CheckoutContext } from '@/contexts/CheckoutContext';
import useSettings from '@/hooks/useSettings';
import { PaymentMethodOption, ProductData } from '@/types';
import { getLabel } from '@/utils/brand';
import { applyTransparency } from '@/utils/colors';
import { formatPrice } from '@/utils/format';
import { getRecurrencePeriodDescription, getRecurrencePeriodLabel } from '@/utils/payment';
import { useContext } from 'react';

type ProductPurchaseProps = {
  isLoading?: boolean;
  price: number;
  product: ProductData['product'];
  sellerEmail: string;
  type: string;
  recurrencePeriod?: number;
  paymentMethods?: PaymentMethodOption[];
};

const ProductPurchaseComponent = ({
  isLoading,
  price = 0,
  product,
  sellerEmail,
  type,
  recurrencePeriod
}: ProductPurchaseProps) => {
  const settings = useSettings();

  const { offer, canBePaidInInstallments } = useContext(CheckoutContext);

  if (isLoading) {
    return (
      <div
        style={{
          backgroundColor: settings.form?.background.color
        }}
        className="max-w-md mx-auto bg-gray-800 text-white rounded-md overflow-hidden shadow-md"
      >
        <div
          style={{
            backgroundColor: settings.payButton.color
          }}
          className="bg-[#0F7864] p-4 text-center"
        >
          <h1
            style={{
              color: settings.payButton.text.color
            }}
            className="text-xl font-semibold"
          >
            Compra segura
          </h1>
        </div>

        <div className="p-6 flex">
          <svg
            className="w-24 h-24 object-cover rounded-md mr-4 dark:bg-gray-300  bg-gray-200 animate-pulse"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            fill="currentColor"
            viewBox="-1 -3 18 26"
          >
            <path d="M14.066 0H7v5a2 2 0 0 1-2 2H0v11a1.97 1.97 0 0 0 1.934 2h12.132A1.97 1.97 0 0 0 16 18V2a1.97 1.97 0 0 0-1.934-2ZM10.5 6a1.5 1.5 0 1 1 0 2.999A1.5 1.5 0 0 1 10.5 6Zm2.221 10.515a1 1 0 0 1-.858.485h-8a1 1 0 0 1-.9-1.43L5.6 10.039a.978.978 0 0 1 .936-.57 1 1 0 0 1 .9.632l1.181 2.981.541-1a.945.945 0 0 1 .883-.522 1 1 0 0 1 .879.529l1.832 3.438a1 1 0 0 1-.031.988Z" />
            <path d="M5 5V.13a2.96 2.96 0 0 0-1.293.749L.879 3.707A2.98 2.98 0 0 0 .13 5H5Z" />
          </svg>
          <div>
            <div className="h-[15px] bg-gray-200 rounded-full dark:bg-gray-300 w-40 animate-pulse"></div>
            <p
              style={{ color: applyTransparency(settings.text.color.secondary || '', 0.5) }}
              className="text-sm text-gray-400 mt-2"
            >
              Precisa de ajuda?
            </p>
            <a
              href={`mailto:${sellerEmail}`}
              target="_blank"
              className="text-blue-400 underline text-sm"
            >
              Veja o contato do vendedor
            </a>
          </div>
        </div>

        <div className="border-t-2 border-dashed border-[#919EAB33]">
          <div className="p-6">
            <h3 style={{ color: settings.text.color.primary }} className="text-xl font-semibold">
              Total
            </h3>

            <div
              style={{ color: settings.text.color.active }}
              className="text-2xl font-bold text-green-400"
            >
              <div className="h-[30px] bg-gray-200 rounded-full dark:bg-gray-300 w-40 animate-pulse"></div>
            </div>
            {type !== 'subscription' && (
              <div
                style={{
                  color: canBePaidInInstallments
                    ? applyTransparency(settings.text.color.secondary || '', 0.7)
                    : settings.text.color.active
                }}
                className="text-gray-300 mt-2"
              >
                <div className="h-[16px] bg-gray-200 rounded-full dark:bg-gray-300 w-36 animate-pulse"></div>
              </div>
            )}
            <div
              style={{ color: applyTransparency(settings.text.color.secondary || '', 0.5) }}
              className="text-gray-500 text-sm mt-2"
            >
              <div className="h-[12px] bg-gray-200 rounded-full dark:bg-gray-300 w-24 animate-pulse"></div>
            </div>
          </div>
        </div>

        <div
          style={{ color: applyTransparency(settings.text.color.secondary || '', 0.5) }}
          className="border-t-2 border-dashed border-[#919EAB33] p-4 text-center text-xs text-gray-400"
        >
          <img
            src='logo/cakto-logo-full-new.png'
            alt="cakto"
            className="mx-auto"
            width={100}
          />
          <p>{getLabel.brandName()} está processando este pagamento para o vendedor </p>
          <p>Este site é protegido pelo reCAPTCHA do Google</p>
          <a
            href="https://cakto.com.br/politica-de-privacidade"
            target="_blank"
            className="underline"
          >
            Política de privacidade
          </a>{' '}
          e{' '}
          <a href="https://cakto.com.br/termos-e-condicoes/" target="_blank" className="underline">
            Termos de serviço
          </a>
          <p
            style={{ color: applyTransparency(settings.text.color.secondary || '', 0.4) }}
            className="mt-2 text-gray-500"
          >
            * Parcelamento com acréscimo
          </p>
          <p
            style={{ color: applyTransparency(settings.text.color.secondary || '', 0.4) }}
            className="text-gray-500"
          >
            Ao continuar, você concorda com os{' '}
            <a
              style={{ color: applyTransparency(settings.text.color.secondary || '', 0.4) }}
              href="#"
              className="underline"
            >
              Termos de Compra
            </a>
          </p>
        </div>
      </div>
    );
  }

  const image = product?.image;
  const calculatedInstallments = product?.calculatedInstallments || [];
  const name = product?.name || '';
  const productPrice = product?.productPrice || 0;

  const { installment: maxInstallment, value } = calculatedInstallments.reduce(
    (maxItem: any, item: any) => {
      return item.installment > maxItem.installment ? item : maxItem;
    },
    { installment: 0, value: 0 }
  );

  const getPrice = () => {
    if (type === 'subscription') {
      return `${formatPrice(price)} / ${getRecurrencePeriodLabel(recurrencePeriod || 30)}`;
    }

    if (!canBePaidInInstallments) {
      return '';
    }

    return `${maxInstallment} X de ${formatPrice(value)}`;
  };

  const getRenovation = () => {
    if (type === 'subscription') {
      return getRecurrencePeriodDescription(recurrencePeriod || 30);
    }

    return ``;
  };

  return (
    <div
      style={{
        backgroundColor: settings.form?.background.color
      }}
      className="max-w-md mx-auto bg-gray-800 text-white rounded-md overflow-hidden shadow-md"
    >
      <div
        style={{
          backgroundColor: settings.payButton.color
        }}
        className="bg-[#0F7864] p-4 text-center"
      >
        <h1
          style={{
            color: settings.payButton.text.color
          }}
          className="text-xl font-semibold"
        >
          Compra segura
        </h1>
      </div>

      <div className="p-6 flex">
        {image && (
          <img
            src={image}
            alt="Imagem do produto"
            className="w-24 h-24 object-cover rounded-md mr-4"
          />
        )}
        <div>
          <h2 style={{ color: settings.text.color.primary }} className="text-xl font-bold">
            {name}
          </h2>
          <p
            style={{ color: applyTransparency(settings.text.color.secondary || '', 0.5) }}
            className="text-sm text-gray-400 mt-2"
          >
            Precisa de ajuda?
          </p>
          <a
            href={`mailto:${sellerEmail}`}
            target="_blank"
            className="text-blue-400 underline text-sm"
          >
            Veja o contato do vendedor
          </a>
        </div>
      </div>

      <div className="border-t-2 border-dashed border-[#919EAB33]">
        <div className="p-6">
          <h3 style={{ color: settings.text.color.primary }} className="text-xl font-semibold">
            Total
          </h3>
          {productPrice > price && (
            <div className="mt-4 text-gray-400 line-through">{`De ${formatPrice(productPrice)}`}</div>
          )}
          <div
            style={{ color: settings.text.color.active }}
            className="text-2xl font-bold text-green-400"
          >
            {canBePaidInInstallments && "Em até"} {getPrice()}
          </div>
          {type !== 'subscription' && (
            <div
              style={{
                color: canBePaidInInstallments
                  ? applyTransparency(settings.text.color.secondary || '', 0.7)
                  : settings.text.color.active
              }}
              className="text-gray-300 mt-2"
            >{`${canBePaidInInstallments ? 'ou' : ''} ${formatPrice(price)} à vista`}</div>
          )}
          <div
            style={{ color: applyTransparency(settings.text.color.secondary || '', 0.5) }}
            className="text-gray-500 text-sm mt-2"
          >
            {getRenovation()}
          </div>
        </div>
      </div>

      <div
        style={{ color: applyTransparency(settings.text.color.secondary || '', 0.5) }}
        className="border-t-2 border-dashed border-[#919EAB33] p-4 text-center text-xs text-gray-400"
      >
        <img
          src='logo/cakto-logo-full-new.png'
          alt="cakto"
          className="mx-auto"
          width={100}
        />
        <p>{getLabel.brandName()} está processando este pagamento para o vendedor {offer?.sellerName}</p>
        <p>Este site é protegido pelo reCAPTCHA do Google</p>
        <a
          href="https://www.cakto.com.br/politica-de-privacidade"
          target="_blank"
          className="underline"
        >
          Política de privacidade
        </a>{' '}
        e{' '}
        <a href="https://cakto.com.br/termos-e-condicoes/" target="_blank" className="underline">
          Termos de serviço
        </a>
        <p
          style={{ color: applyTransparency(settings.text.color.secondary || '', 0.4) }}
          className="mt-2 text-gray-500"
        >
          * Parcelamento com acréscimo
        </p>
        <p
          style={{ color: applyTransparency(settings.text.color.secondary || '', 0.4) }}
          className="text-gray-500"
        >
          Ao continuar, você concorda com os{' '}
          <a
            style={{ color: applyTransparency(settings.text.color.secondary || '', 0.4) }}
            href="https://www.cakto.com.br/termo-de-compra-e-venda"
            className="underline"
          >
            Termos de Compra
          </a>
        </p>
      </div>
    </div>
  );
};

export default ProductPurchaseComponent;
